/**
 * Ошибки getUserMedia/getDisplayMedia по-русски. Браузер отдаёт их одной строкой вроде
 * «Could not start audio source», и без перевода человек видит английский текст и не понимает,
 * что делать: чаще всего устройство просто занято другой программой.
 */

export type MediaKind = 'mic' | 'camera' | 'screen'

const DEVICE: Record<MediaKind, string> = { mic: 'Микрофон', camera: 'Камера', screen: 'Демонстрация экрана' }

/** Имя ошибки DOMException; у части браузеров оно приходит только в тексте. */
function errName(err: unknown): string {
  if (err && typeof err === 'object' && 'name' in err && typeof (err as { name: unknown }).name === 'string') {
    return (err as { name: string }).name
  }
  return ''
}

function errText(err: unknown): string {
  return err instanceof Error ? err.message : ''
}

/** Устройство занято другой программой — самый частый случай «Could not start audio source». */
export function isBusyDevice(err: unknown): boolean {
  const name = errName(err)
  return name === 'NotReadableError' || name === 'AbortError' || /could not start|failed to allocate/i.test(errText(err))
}

/** Выбранное устройство исчезло или сменило идентификатор: стоит взять то, что есть. */
export function isMissingDevice(err: unknown): boolean {
  const name = errName(err)
  return name === 'OverconstrainedError' || name === 'ConstraintNotSatisfiedError' || name === 'NotFoundError' || name === 'DevicesNotFoundError'
}

/**
 * Техническая строка для показа мелким шрифтом: имя ошибки и текст как есть.
 * Без неё «микрофон занят» не отличить от «Windows не дал доступ» — а лечатся они по-разному.
 */
export function mediaErrorDetails(err: unknown): string {
  const name = errName(err)
  const text = errText(err)
  return [name, text].filter(Boolean).join(': ')
}

/**
 * Подсказка под ошибкой для конкретной системы. На Windows «устройство не читается»
 * почти всегда значит не «занято», а «система не пустила»: доступ к микрофону там
 * выдаётся отдельно, и отдельно ещё раз — «классическим» программам.
 */
export function mediaErrorHint(err: unknown, kind: MediaKind, platform: string): string | null {
  if (kind === 'screen' || platform !== 'win32') return null
  if (!isBusyDevice(err) && !isDenied(err)) return null
  const what = kind === 'camera' ? 'камере' : 'микрофону'
  const page = kind === 'camera' ? 'Камера' : 'Микрофон'
  /*
   * Про «Кличата нет в списке» надо сказать прямо: классические программы Windows
   * поимённо туда не добавляет, их покрывает один общий переключатель ниже. Без этого
   * человек открывает настройки, не находит себя в списке и решает, что дело в другом.
   */
  return `«Параметры» → «Конфиденциальность и защита» → «${page}». Включите «Доступ к ${what}», а ниже — «Разрешить классическим приложениям доступ к ${what}». Кличата в списке приложений не ищите: он классическая программа, и его покрывает именно этот отдельный переключатель. Если разрешения на месте, а в браузере устройство работает — дело в антивирусе. Kaspersky, ESET, Avast закрывают камеру и микрофон программам, которых не знают, а Кличат пока без подписи издателя и попадает в недоверенные. В Касперском: «Защита камеры и микрофона» — разрешить Кличату, либо «Предотвращение вторжений» — перевести Кличат в доверенные.`
}

/** Пользователь закрыл окно выбора или не дал доступ. */
export function isDenied(err: unknown): boolean {
  return errName(err) === 'NotAllowedError' || /permission denied/i.test(errText(err))
}

/** У демонстрации свои формулировки: «экран занят» звучит дико, там речь про источник и качество. */
function describeScreenError(err: unknown, platform: string): string {
  const raw = errText(err)
  if (isDenied(err)) return 'Доступ к экрану не выдан: разрешите запись экрана в настройках системы и попробуйте снова'
  /*
   * «Занято другой программой» — объяснение для микрофона, к окну оно не подходит: окно
   * не занимают, его либо отдают, либо нет. Chromium в тексте пишет, что именно не
   * запустилось — звук или картинку, — и по этому их и различаем.
   */
  if (isBusyDevice(err)) {
    if (/audio/i.test(raw)) {
      return 'Не удалось начать демонстрацию со звуком компьютера: звук занят другой программой. Попробуйте ещё раз или отметьте «Заглушить звук трансляции»'
    }
    /*
     * Причины разные по системам, и общей формулировкой тут не обойтись. На маке самая
     * частая — разрешение выдано, но процесс его ещё не видит: macOS спрашивает систему
     * один раз при запуске. А Кличат живёт в трее, и закрыть окно — не значит выйти.
     */
    /*
     * На маке это почти всегда разрешение, причём заново выданное не помогает. Приложение
     * пока без сертификата Apple, а неподписанному macOS привязывает разрешение к самому
     * файлу: после обновления галочка в списке остаётся, но относится она к прежней сборке.
     * Лечится только удалением строки и добавлением заново — что и проверено на практике.
     */
    if (platform === 'darwin') {
      return 'Не удалось начать демонстрацию. Откройте «Конфиденциальность и безопасность» → «Запись экрана», удалите Кличат из списка кнопкой «−», добавьте заново кнопкой «+» и выйдите из Кличата через значок в трее. Галочка в списке может стоять и при этом не работать: приложение пока без сертификата Apple, и после обновления macOS считает его новым'
    }
    return 'Не удалось начать демонстрацию: выбранное окно не отдаёт картинку. Чаще всего оно свёрнуто, уже закрыто или запущено от имени администратора — разверните его и попробуйте снова'
  }
  switch (errName(err)) {
    case 'NotFoundError':
      return 'Не удалось начать демонстрацию: выбранный экран или окно больше недоступны'
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return 'Не удалось начать демонстрацию с таким качеством — выберите разрешение или частоту кадров пониже'
    default:
      return raw ? `Не удалось начать демонстрацию: ${raw}` : 'Не удалось начать демонстрацию'
  }
}

export function describeMediaError(err: unknown, kind: MediaKind, platform = ''): string {
  if (kind === 'screen') return describeScreenError(err, platform)
  const what = DEVICE[kind]
  const raw = errText(err)
  switch (errName(err)) {
    case 'NotAllowedError':
      return `${what}: доступ запрещён. Разрешите его в настройках системы`
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return `${what} не найден${kind === 'camera' ? 'а' : ''}: проверьте, что устройство подключено`
    case 'NotReadableError':
    case 'AbortError':
      /*
       * На Windows «устройство не читается» почти всегда значит не «занято», а «система
       * не пустила»: доступ там выдаётся отдельно приложениям и отдельно ещё раз
       * классическим программам. Ставим вероятную причину первой — иначе человек идёт
       * закрывать Zoom, которого и не было запущено.
       */
      if (platform === 'win32') {
        return `${what} недоступен${kind === 'camera' ? 'а' : ''}: скорее всего Windows не дал доступ. Проверьте разрешения — или закройте программу, которая могл${kind === 'camera' ? 'а' : ''} занять устройство`
      }
      return `${what} занят${kind === 'camera' ? 'а' : ''} другой программой. Закройте её (Zoom, OBS, другая программа связи) и попробуйте снова`
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return `${what}: выбранное устройство недоступно — выберите другое в «Голос и видео»`
    case 'SecurityError':
      return `${what}: доступ заблокирован, нужно защищённое соединение`
    default:
      if (isBusyDevice(err)) {
        return platform === 'win32'
          ? `${what} недоступен${kind === 'camera' ? 'а' : ''}: скорее всего Windows не дал доступ`
          : `${what} занят${kind === 'camera' ? 'а' : ''} другой программой. Закройте её и попробуйте снова`
      }
      return raw ? `${what} недоступен: ${raw}` : `${what} недоступен`
  }
}
